import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { UpsaleService } from '../../api/upsale.service';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { CreateQuestionComponent } from '../../content/products/product-upsale/create-question/create-question.component';
import { MobileLayoutService } from '../../services/mobile-layout.service';
import { UpsaleQuestionsPanelService } from '../../services/upsale-questions-panel.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

/**
 * Side panel d'attache des questions upsale à un produit (remplace l'ancien
 * dialogue DragDropLists). Attach/detach sont immédiats (comme avant) ; le
 * réordonnancement de la liste de droite est persisté via move-before/after.
 * lastResponse (payload produit) est retourné à la fermeture.
 */
@Component({
  selector: 'app-upsale-questions-panel',
  templateUrl: './upsale-questions-panel.component.html',
  styleUrls: ['./upsale-questions-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatTooltipModule,
  ],
})
export class UpsaleQuestionsPanelComponent
  extends GT2PageAbstract
  implements OnInit
{
  productUUID!: string;
  productName: string | null = null;
  available: any[] = [];
  attached: any[] = [];
  isLoadingAvailable: boolean = false;
  isLoadingAttached: boolean = false;
  filterValue: string = '';
  lastResponse: any = null;
  newDialogRef: any;
  readonly mobileLayout = inject(MobileLayoutService);

  constructor(
    private panelService: UpsaleQuestionsPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public upsaleService: UpsaleService,
    private dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();
    if (data) {
      this.productUUID = data.productUUID;
      this.productName = data.productName ?? null;
    }
  }

  ngOnInit(): void {
    this.loadAvailable();
    this.loadAttached();
  }

  get filteredAvailable(): any[] {
    const filter = this.filterValue.trim().toLowerCase();
    if (!filter) {
      return this.available;
    }
    return this.available.filter(
      (question) => (question.name || '').toLowerCase().indexOf(filter) !== -1,
    );
  }

  public loadAvailable(): void {
    this.isLoadingAvailable = true;
    this.upsaleService.getAvailableUpsaleQuestions(this.productUUID).subscribe({
      next: (response: any) => {
        this.isLoadingAvailable = false;
        this.available = response.data || [];
      },
      error: (error: any) => {
        this.logger.error(
          'UpsaleQuestionsPanelComponent.loadAvailable() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.isLoadingAvailable = false;
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public loadAttached(): void {
    this.isLoadingAttached = true;
    this.upsaleService.getAttachedQuestions(this.productUUID).subscribe({
      next: (response: any) => {
        this.isLoadingAttached = false;
        this.attached = response.data || [];
      },
      error: (error: any) => {
        this.logger.error(
          'UpsaleQuestionsPanelComponent.loadAttached() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.isLoadingAttached = false;
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  /** Drop dans la liste de gauche (disponibles) : venir de droite = detach. */
  public onAvailableDrop(event: any): void {
    if (event.previousContainer === event.container) {
      return;
    }
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );
    this.detach(event.item.data);
  }

  /** Drop dans la liste de droite : même liste = reorder persisté, sinon attach. */
  public onAttachedDrop(event: any): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.attached, event.previousIndex, event.currentIndex);
      this.persistReorder(event.item.data, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      this.attach(event.item.data);
    }
  }

  public onAddItem(question: any): void {
    for (let i = 0; i < this.available.length; ++i) {
      if (this.available[i].uuid === question.uuid) {
        this.available.splice(i, 1);
        break;
      }
    }
    this.attached.push(question);
    this.attach(question);
  }

  public onRemoveItem(question: any): void {
    for (let i = 0; i < this.attached.length; ++i) {
      if (this.attached[i].uuid === question.uuid) {
        this.attached.splice(i, 1);
        break;
      }
    }
    this.available.push(question);
    this.detach(question);
  }

  private attach(question: any): void {
    this.upsaleService.attach(this.productUUID, question.uuid).subscribe({
      next: (response: any) => {
        this.lastResponse = response;
      },
      error: (error: any) => {
        this.logger.error(
          'UpsaleQuestionsPanelComponent.attach() -> ERROR: ' +
            JSON.stringify(error),
        );
        // undo
        this.attached = this.attached.filter(
          (item) => item.uuid !== question.uuid,
        );
        this.available.push(question);
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  private detach(question: any): void {
    this.upsaleService.deattach(this.productUUID, question.uuid).subscribe({
      next: (response: any) => {
        this.lastResponse = response;
      },
      error: (error: any) => {
        this.logger.error(
          'UpsaleQuestionsPanelComponent.detach() -> ERROR: ' +
            JSON.stringify(error),
        );
        // undo
        this.available = this.available.filter(
          (item) => item.uuid !== question.uuid,
        );
        this.attached.push(question);
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  /** Persiste l'ordre après un drag dans la liste de droite. */
  private persistReorder(question: any, index: number): void {
    if (this.attached.length < 2) {
      return;
    }
    const request =
      index === 0
        ? this.upsaleService.productQuestionMoveBefore(
            this.productUUID,
            question.uuid,
            this.attached[1].uuid,
          )
        : this.upsaleService.productQuestionMoveAfter(
            this.productUUID,
            question.uuid,
            this.attached[index - 1].uuid,
          );
    this.isLoadingAttached = true;
    request.subscribe({
      next: (response: any) => {
        this.isLoadingAttached = false;
        this.lastResponse = response;
        if (response?.data?.upsale_questions) {
          this.attached = response.data.upsale_questions;
        }
      },
      error: (error: any) => {
        this.logger.error(
          'UpsaleQuestionsPanelComponent.persistReorder() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.isLoadingAttached = false;
        this.loadAttached();
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public onCreateQuestion(): void {
    this.newDialogRef = this.dialog.open(CreateQuestionComponent, {
      width: '440px',
      minWidth: 300,
      maxHeight: '96vh',
      panelClass: 'scrollable-dialog',
      disableClose: false,
      data: {},
    });

    this.newDialogRef.afterClosed().subscribe({
      next: (result: any) => {
        if (result) {
          this.loadAvailable();
        }
        this.newDialogRef = null;
      },
    });
  }

  public clearSearch(): void {
    this.filterValue = '';
  }

  public close(): void {
    this.panelService.emitResult(this.lastResponse ?? false);
  }
}
